// `CaptureFromExtBanner` — US-153 gap-design surface.
//
// Listens for `lead.pushed` realtime events on the `UserHubDO` channel and
// renders an in-page banner whenever a new lead lands while the user is on
// the leads table. The banner offers a "Voir" CTA that scrolls + selects
// the freshly-captured row.
//
// Why piggyback on `lead.pushed`?
//   The single existing realtime event for "a lead row exists now" is
//   `lead.pushed` (emitted by `POST /conversations/:id/push-to-leads`,
//   `POST /leads` from the extension once Wave 6 wires it, and any future
//   Slack/Discord canal handlers). Using it means the banner already lights
//   up today for the inbox→leads round-trip, and the extension capture
//   path (Ext Wave G) only needs to call the same emitter.
//
// Lifecycle:
//   - Mounted as a child of `<LeadsTablePage>`.
//   - Subscribes via `useUserHubChannel('lead.pushed', cb)` and stashes the
//     last 1 captured leadId in local state.
//   - Auto-dismisses after 8s, or on user click of × / "Voir".
//   - Calls `useLeads`'s query invalidation through the realtime handler so
//     the row is in the cache by the time "Voir" focuses it.

import { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';

import { useT } from '@barel/i18n';
import { useUserHubChannel } from '@barel/api-client/react';
import { Banner } from '@barel/ui';

interface CapturedEvent {
  leadId: string;
  conversationId?: string;
  capturedAt: number;
}

export interface CaptureFromExtBannerProps {
  /** Callback fires when the user clicks "Voir" on the banner. */
  onView: (leadId: string) => void;
  /** Auto-dismiss timeout in ms (default 8000). */
  autoDismissMs?: number;
}

export function CaptureFromExtBanner({ onView, autoDismissMs = 8000 }: CaptureFromExtBannerProps) {
  const t = useT('leads');
  const [event, setEvent] = useState<CapturedEvent | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribe to the per-user fan-out channel.
  useUserHubChannel('lead.pushed', (frame: { type?: string; payload?: unknown }) => {
    if (frame.type !== 'lead.pushed') return;
    const payload = frame.payload as { leadId?: string; conversationId?: string } | undefined;
    if (!payload?.leadId) return;
    setEvent({
      leadId: payload.leadId,
      conversationId: payload.conversationId,
      capturedAt: Date.now(),
    });
  });

  // Auto-dismiss.
  useEffect(() => {
    if (!event) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setEvent(null);
    }, autoDismissMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [event, autoDismissMs]);

  if (!event) return null;

  return (
    <Banner
      data-testid="capture-banner"
      tone="info"
      variant="soft"
      title={t('capture.bannerTitle')}
      onDismiss={() => setEvent(null)}
      dismissAriaLabel={t('capture.dismiss')}
      action={
        <button
          type="button"
          onClick={() => {
            onView(event.leadId);
            setEvent(null);
          }}
          data-testid="capture-banner-view"
          className="inline-flex items-center gap-1 text-xs font-semibold text-info underline-offset-2 hover:underline"
        >
          <ExternalLink size={12} aria-hidden="true" />
          {t('capture.view')}
        </button>
      }
    >
      {t('capture.bannerDescription')}
    </Banner>
  );
}
